import React, { useState, useEffect } from 'react';
import { Modal, Button, Row, Col, Form, Card, Table } from 'react-bootstrap';
import * as XLSX from 'xlsx';
import { api } from "../../../services/api";
import Swal from "sweetalert2";
import { FaFileUpload } from "react-icons/fa";

export default function ModalImportacao({ show, onHide }) {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [lookups, setLookups] = useState({ roteamentos: [], bandeiras: [], condicoes: [] });
    const [form, setForm] = useState({ roteamento: '', bandeira: '', condicao: '', taxa: 0 });

    useEffect(() => {
        if (show) {
            api.get('/financeiro/conciliacao/roteamentos')
                .then(res => setLookups(prev => ({ ...prev, roteamentos: res.data })))
                .catch(err => console.error("Erro ao carregar roteamentos", err));
        } else {
            setForm({ roteamento: '', bandeira: '', condicao: '', taxa: 0 });
            setData([]);
        }
    }, [show]);

    const normalizar = (s) => {
        return String(s || '')
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, '')
            .replace(/\?/g, '')
            .replace(/[^a-z0-9]/gi, '')
            .toLowerCase()
            .trim();
    };

    const getVal = (row, ...nomes) => {
        const keys = Object.keys(row);
        for (let nome of nomes) {
            const alvo = normalizar(nome);
            const key = keys.find(k => {
                const nk = normalizar(k);
                return (
                    nk === alvo ||
                    nk.includes(alvo) ||
                    alvo.includes(nk)
                );
            });
            if (key !== undefined && row[key] !== undefined) return row[key];
        }
        return '';
    };

    const formatarDataBR = (valor) => {
        if (!valor) return '';

        if (typeof valor === 'string' && valor.includes('GMT')) {
            const d = new Date(valor);
            if (!isNaN(d)) {
                const dia = String(d.getDate()).padStart(2, '0');
                const mes = String(d.getMonth() + 1).padStart(2, '0');
                const ano = d.getFullYear();
                return `${dia}/${mes}/${ano}`;
            }
        }

        if (valor instanceof Date) {
            const dia = String(valor.getDate()).padStart(2, '0');
            const mes = String(valor.getMonth() + 1).padStart(2, '0');
            const ano = valor.getFullYear();
            return `${dia}/${mes}/${ano}`;
        }

        const s = String(valor).trim();

        if (/^\d{2}\/\d{2}\/\d{4}/.test(s)) {
            return s.substring(0, 10);
        }

        if (s.includes('-')) {
            const parts = s.split('T')[0].split('-');
            if (parts.length === 3) {
                const [y, m, d] = parts;
                return `${d}/${m}/${y}`;
            }
        }

        return s;
    };

    const formatarFloat = (valor) => {
        if (valor === undefined || valor === null || valor === '') return 0;
        let s = String(valor).replace(/"/g, '').trim();

        if (s.includes('.') && s.includes(',')) {
            if (s.lastIndexOf(',') > s.lastIndexOf('.')) {
                s = s.replace(/\./g, '').replace(',', '.');
            } else {
                s = s.replace(/,/g, '');
            }
        } else if (s.includes(',')) {
            s = s.replace(',', '.');
        }

        return parseFloat(s) || 0;
    };

    const handleRoteamento = async (val) => {
        setForm({ roteamento: val, bandeira: '', condicao: '', taxa: 0 });
        if (!val) return;
        try {
            const res = await api.get(`/financeiro/conciliacao/bandeiras?roteamento=${val}`);
            setLookups(prev => ({ ...prev, bandeiras: ["Usar do arquivo", ...res.data], condicoes: [] }));
        } catch (err) {
            Swal.fire("Erro", "Erro ao buscar bandeiras", "error");
        }
    };

    const handleBandeira = async (val) => {
        setForm(prev => ({ ...prev, bandeira: val, condicao: '', taxa: 0 }));
        if (!val) return;
        try {
            const res = await api.get(`/financeiro/conciliacao/condicoes?roteamento=${form.roteamento}&bandeira=${val}`);
            setLookups(prev => ({ ...prev, condicoes: res.data }));
        } catch (err) {
            console.error(err);
        }
    };

    const handleCondicao = async (val) => {
        setForm(prev => ({ ...prev, condicao: val }));
        if (!val) return;
        try {
            const res = await api.get(`/financeiro/conciliacao/taxa?roteamento=${form.roteamento}&bandeira=${form.bandeira}&condicao=${val}`);
            setForm(prev => ({ ...prev, condicao: val, taxa: res.data.taxa || 0 }));
        } catch (err) {
            console.error(err);
        }
    };

    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const reader = new FileReader();

        reader.onload = (evt) => {
            const decoder = new TextDecoder("iso-8859-1");
            const text = decoder.decode(evt.target.result);

            let linhas = text.split(/\r?\n/).filter(l => l.trim() !== "");
            if (linhas.length === 0) return;

            let headerIndex = 0;
            if (linhas[0].toLowerCase().startsWith("sep=") || !linhas[0].includes(";")) {
                headerIndex = 1;
            }

            if (linhas.length <= headerIndex) return;

            const header = linhas[headerIndex].split(";").map(h => h.replace(/"/g, "").trim());

            const json = linhas.slice(headerIndex + 1).map(linha => {
                const cols = linha.split(";");
                const obj = {};
                header.forEach((h, i) => {
                    obj[h] = cols[i] !== undefined ? cols[i].replace(/"/g, "").trim() : "";
                });
                return obj;
            });

            setData(json);
        };

        reader.readAsArrayBuffer(file);
    };

    const handleSincronizar = async () => {
        if (!form.condicao || data.length === 0) {
            Swal.fire("Atenção", "Preencha todos os filtros e selecione o arquivo", "warning");
            return;
        }

        const getSafeCell = (row, index, ...names) => {
            const keys = Object.keys(row);
            for (const name of names) {
                const foundKey = keys.find(k => k.trim() === name);
                if (foundKey !== undefined) return row[foundKey];
            }
            const values = Object.values(row);
            return values[index] !== undefined ? values[index] : '';
        };

        setLoading(true);
        try {
            const payload = {
                taxa: form.taxa,
                itens: data.map(i => {
                    let bandeiraCalculada = form.bandeira;

                    if (form.bandeira === "Usar do arquivo") {
                        let produtoArquivo =
                            getVal(i, 'Produto', 'PRODUTO', 'Bandeira', 'BANDEIRA') || '';

                        if (!produtoArquivo) {
                            const valores = Object.values(i);
                            produtoArquivo = valores[5] || '';
                        }

                        bandeiraCalculada = String(produtoArquivo).trim();
                    }
                    const values = Object.values(i);
                    const dataCredito = getVal(i, 'Data Crédito', 'Data Credito', 'Data Cred.') || values[7];
                    const dataCreditoOri = getVal(i, 'Data Créd. Orig.', 'Data Credito Orig', 'Data Cred. Orig.') || values[8];

                    return {
                        ROTEAMENTO: form.roteamento,
                        LOJA: parseInt(getSafeCell(i, 2, 'Loja') || 0),
                        DATAVENDA: formatarDataBR(getVal(i, 'Data Venda', 'Data Venda ')),
                        BANDEIRA: bandeiraCalculada,
                        DATACREDITO: formatarDataBR(dataCredito),
                        DATACREDORI: formatarDataBR(dataCreditoOri),
                        NUMCOMPROVANTE: String(
                            getVal(
                                i,
                                'Número Comprovante',
                                'Numero Comprovante',
                                'N?mero Comprovante',
                                'Nmero Comprovante',
                                'N?ero Comprovante',
                                'Comprovante'
                            )
                        ),
                        NSU: parseInt(getVal(i, 'NSU TEF', 'NSU') || 0),
                        CODAUTORIZACAO: String(getVal(i, 'Autorização', 'Codigo Autorizacao', 'Autorizao', 'Autorizacao')),
                        VALORBRUTO: formatarFloat(getVal(i, 'Valor Bruto')),
                        VALORCOMISSAO: formatarFloat(getVal(i, 'Valor Comissão', 'Valor Comissao', 'Valor Comiss?') || 0),
                        VALORLIQUIDO: formatarFloat(getVal(i, 'Valor Líquido', 'Valor lquido', 'Valor Liquido', 'Valor L?uido') || 0),
                        VALORLIQUIDOORIGINAL: formatarFloat(getVal(i, 'Valor Líquido Original', 'Valor Liquido Original', 'Valor Lquido Original', 'Valor L?uido Original') || 0),
                        PARCELA: parseInt(getVal(i, 'Parcela') || 1),
                        TOTALPARCELA: parseInt(getVal(i, 'Total de Parcelas', 'Total Parcelas') || 1),
                        CAPTURA: String(getVal(i, 'Captura') || ''),
                        TIPOLANCAMENTO: String(getVal(i, 'Tipo de Lançamento', 'Tipo Lançamento', 'Tipo Lançam.') || ''),
                        DATAGERACAO: formatarDataBR(new Date()),
                        TAXAFLOR: form.bandeira === "Usar do arquivo" ? "USAR_ARQUIVO" : form.taxa,
                        CONDICAO: form.condicao
                    };
                })
            };

            await api.post('/financeiro/conciliacao/importar', payload);
            Swal.fire("Sucesso", "Importação realizada com sucesso!", "success");
            onHide();
        } catch (err) {
            const msgErro = err.response?.data?.erro || "Falha na comunicação com o servidor";
            Swal.fire("Erro na Importação", msgErro, "error");
        } finally {
            setLoading(false);
        }
    };

    const renderOptions = (lista, campo) => {
        return lista.map((item, index) => {
            const valor = typeof item === 'object' ? item[campo] : item;
            return <option key={index} value={valor}>{valor}</option>;
        });
    };

    return (
        <Modal show={show} onHide={onHide} size="xl" centered backdrop="static">
            <Modal.Header closeButton style={{ background: 'linear-gradient(135deg, #8b0000 0%, #a91b0d 50%, #c0392b 100%)', color: '#ffffff' }} className="py-2 px-3 border-0">
                <Modal.Title className="fs-6 fw-bold d-flex align-items-center gap-2 text-white">
                    <FaFileUpload /> IMPORTAÇÃO DE ARQUIVO DE CONCILIAÇÃO
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="bg-light p-3">
                <Card className="p-3 shadow-sm border-0 rounded-3 mb-3">
                    <Row className="g-2">
                        <Col md={3}>
                            <Form.Label className="small fw-bold text-secondary mb-1">Roteamento</Form.Label>
                            <Form.Select size="sm" value={form.roteamento} onChange={e => handleRoteamento(e.target.value)}>
                                <option value="">Selecione...</option>
                                {renderOptions(lookups.roteamentos, 'ROTEAMENTO')}
                            </Form.Select>
                        </Col>
                        <Col md={3}>
                            <Form.Label className="small fw-bold text-secondary mb-1">Bandeira</Form.Label>
                            <Form.Select size="sm" value={form.bandeira} onChange={e => handleBandeira(e.target.value)}>
                                <option value="">Selecione...</option>
                                {renderOptions(lookups.bandeiras, 'BANDEIRA')}
                            </Form.Select>
                        </Col>
                        <Col md={3}>
                            <Form.Label className="small fw-bold text-secondary mb-1">Condição</Form.Label>
                            <Form.Select size="sm" value={form.condicao} onChange={e => handleCondicao(e.target.value)}>
                                <option value="">Selecione...</option>
                                {renderOptions(lookups.condicoes, 'CONDICAO')}
                            </Form.Select>
                        </Col>
                        <Col md={3}>
                            <Form.Label className="small fw-bold text-danger text-center d-block mb-1">Taxa do Roteamento</Form.Label>
                            <div className="form-control form-control-sm text-center fw-bold bg-light border-danger text-danger">
                                {form.taxa.toFixed(2)}%
                            </div>
                        </Col>
                    </Row>

                    <div className="mt-3">
                        <Form.Label className="small fw-bold text-secondary mb-1">Selecionar Arquivo (CSV)</Form.Label>
                        <Form.Control type="file" size="sm" accept=".csv" onChange={handleFileUpload} />
                    </div>
                </Card>

                <Card className="shadow-sm border-0 rounded-3 overflow-hidden">
                    <div style={{ maxHeight: '280px', overflowY: 'auto' }}>
                        <Table striped size="sm" hover className="mb-0 align-middle" style={{ fontSize: '0.72rem' }}>
                            <thead className="table-secondary sticky-top">
                                <tr>
                                    {data.length > 0 && Object.keys(data[0]).map(k => <th key={k} className="px-2 py-1">{k}</th>)}
                                </tr>
                            </thead>
                            <tbody>
                                {data.slice(0, 50).map((row, i) => (
                                    <tr key={i}>
                                        {Object.values(row).map((v, j) => <td key={j} className="px-2 py-1">{String(v)}</td>)}
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                    {data.length > 50 && <div className="text-center p-2 small text-muted bg-white border-top">Exibindo apenas as primeiras 50 linhas para pré-visualização...</div>}
                </Card>
            </Modal.Body>
            <Modal.Footer className="bg-white border-top-0 py-2">
                <Button variant="secondary" size="sm" className="fw-semibold" onClick={onHide} disabled={loading}>
                    CANCELAR
                </Button>
                <Button style={{ backgroundColor: '#8b0000', borderColor: '#8b0000' }} size="sm" className="fw-bold px-3 text-white" onClick={handleSincronizar} disabled={loading || data.length === 0}>
                    {loading ? 'PROCESSANDO...' : 'INICIAR IMPORTAÇÃO'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}